import { useEffect, useState } from "react";
import ModalLayout from "../layouts/ModalLayout";
import DataTable, { Column } from "../DataTable";
import CustomButton from "../CustomButton";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store";
import { fetchStaff } from "../../features/staff/staffSlice";
import { useBranchCode } from "../../util/hooks/useBranch";
import { AddStaffForm } from "../../types/staff";
import QueryApis from "../../api/QueryApis";
import { formatRoleForDisplay } from "../../util/functions/UniversalFunctions";

interface Props {
  queryId?: number;
  onClose?: () => void;
  onAssigned?: () => void;
}

export default function AssignEnquiryModal({
  queryId,
  onClose,
  onAssigned,
}: Props) {
  const dispatch = useDispatch<AppDispatch>();
  const branchCode = useBranchCode();

  const { staffList, loading } = useSelector((state: RootState) => state.staff);

  const ALLOWED_ROLES = ["ROLE_COUNSELLOR", "ROLE_ADMISSION_COORDINATOR"];

  const [selectedStaff, setSelectedStaff] = useState<number | null>(null);

  useEffect(() => {
    if (!branchCode) return;

    dispatch(
      fetchStaff({
        branchCode,
        page: 0,
        size: 50,
      }),
    );
  }, [dispatch, branchCode]);

  const filteredStaffList = (staffList || []).filter((staff) =>
    ALLOWED_ROLES.includes(staff.roleName),
  );

  const handleAssign = async () => {
    if (!selectedStaff) return;

    const res = await QueryApis.assignQuery(queryId, selectedStaff);

    if (res) {
      onAssigned?.();
      onClose?.();
    }
  };

  const columns: Column<AddStaffForm>[] = [
    {
      label: "",
      accessor: "staffId",
      render: (item) => (
        <input
          type="radio"
          name="staffSelect"
          checked={selectedStaff === item.staffId}
          onChange={() => setSelectedStaff(item.staffId)}
          className="flex items-center justify-center"
        />
      ),
    },
    {
      label: "SL NO.",
      accessor: "staffId",
      render: (_, index) => <p>{index + 1}</p>,
    },
    {
      label: "Profile",
      accessor: "fullName",
      render: (item) => (
        <div>
          {!item?.profile_image_url ? (
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary-600 text-white font-semibold">
              {item.fullName.charAt(0).toUpperCase()}
            </div>
          ) : (
            <img
              src={item?.profile_image_url}
              alt="profile"
              className="w-10 h-10 rounded-full object-cover bg-background-200"
            />
          )}
        </div>
      ),
    },
    {
      label: "Staff Name",
      accessor: "fullName",
      render: (item) => (
        <div className="font-bold text-sm text-neutral-400">
          {item.fullName}
        </div>
      ),
    },
    {
      label: "Designation",
      accessor: "roleName",
      render: (item) => (
        <div className="text-xs text-neutral-300">
          {formatRoleForDisplay(item.roleName) || "----"}
        </div>
      ),
    },
  ];

  return (
    <ModalLayout onClose={onClose} width="max-w-lg">
      <div className="flex flex-col gap-6 max-h-[500px]">
        {filteredStaffList.length === 0 && !loading ? (
          <p className="text-base font-medium text-neutral-500">
            There are no counsellors or admission coordinators available at the
            moment.
          </p>
        ) : (
          <>
            <h4 className="font-semibold text-xl text-neutral-400">
              Select a staff member to assign this enquiry
            </h4>

            <DataTable
              columns={columns}
              data={filteredStaffList}
              isLoading={loading}
            />
          </>
        )}
        <div className="flex justify-end gap-4">
          <CustomButton
            text="Cancel"
            color="bg-neutral-200"
            onClick={onClose}
          />
          <CustomButton
            text="Assign"
            color="bg-primary-500"
            onClick={handleAssign}
            disabled={!selectedStaff}
          />
        </div>
      </div>
    </ModalLayout>
  );
}
